'use client'

import Image from 'next/image'

import { stegaClean } from '@sanity/client/stega'
import { PortableTextBlock } from 'next-sanity'

import PortableText from '@/app/components/PortableText'
import { TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import type { ContentImage as ContentImageType } from '@/sanity.types'
import { dataAttr, urlForImage } from '@/sanity/lib/utils'

type AspectRatio = 'wide' | 'square' | 'portrait' | '74/49'

const aspectRatioConfig: Record<
  AspectRatio,
  {
    ratio: string
    width: number
    height: number
    classes: string
    sizes: string
  }
> = {
  '74/49': {
    ratio: '74/49',
    width: 1010,
    height: 669,
    classes: 'w-full',
    sizes: '100vw',
  },
  'wide': {
    ratio: '16/9',
    width: 1010,
    height: 568,
    classes: 'max-w-full max-h-[200px] md:max-h-[350px] lg:max-w-[1010px] lg:max-h-[568px]',
    sizes: '(max-width: 768px) 100vw, (max-width: 1024px) 768px, 1010px',
  },
  'square': {
    ratio: '1/1',
    width: 505,
    height: 505,
    classes: 'max-w-full max-h-full md:max-w-full md:max-h-full lg:max-w-[505px] lg:max-h-[505px]',
    sizes: '(max-width: 768px) 100vw, 505px',
  },
  'portrait': {
    ratio: '3/4',
    width: 505,
    height: 673,
    classes: 'max-w-full max-h-full md:max-w-full md:max-h-full lg:max-w-[505px] lg:max-h-[673px]',
    sizes: '(max-width: 768px) 100vw, 505px',
  },
}

interface ContentImageProps {
  value: ContentImageType & { _key?: string }
  documentId?: string
  documentType?: string
  parentPath?: string
  tracking?: TrackingLocation
}

export function ContentImage({
  value,
  documentId,
  documentType,
  parentPath,
  tracking,
}: ContentImageProps) {
  const { _key, image, aspectRatio: rawAspectRatio, caption } = value

  if (!image?.asset) {
    return null
  }

  const aspectRatio = (stegaClean(rawAspectRatio) as AspectRatio) || 'wide'
  const config = aspectRatioConfig[aspectRatio]

  const imageUrl = urlForImage(image)?.url()

  if (!imageUrl) {
    return null
  }

  // Create data attribute for presentation mode if we have all required context
  const attr =
    _key && documentId && documentType && parentPath
      ? dataAttr({
          id: documentId,
          type: documentType,
          path: `${parentPath}[_key=="${_key}"]`,
        })
      : undefined

  return (
    <figure className="my-3xl lg:my-5xl" {...(attr && { 'data-sanity': attr.toString() })}>
      <div
        className={cn('relative overflow-hidden', config.classes)}
        style={{ aspectRatio: config.ratio }}
      >
        <Image
          loader={({ width }) => {
            // Generate responsive image URLs based on requested width
            // Calculate height maintaining aspect ratio
            const height = Math.round(width / (config.width / config.height))
            return urlForImage(image)?.width(width).height(height).fit('crop').url() || imageUrl
          }}
          src={imageUrl}
          alt={stegaClean(image.alt) || ''}
          width={config.width}
          height={config.height}
          className="object-cover w-full h-full rounded-sm lg:rounded-md"
          sizes={config.sizes}
          loading="lazy"
        />
      </div>
      {caption && caption.length > 0 && (
        <figcaption
          className={cn(
            'mt-xl text-left size-body-xs lg:size-body-sm text-neutral-600',
            config.classes,
          )}
        >
          <PortableText
            value={caption as PortableTextBlock[]}
            tracking={tracking || { location: 'pageContent', section: 'content-image' }}
            paragraphClassName="!mb-0"
          />
        </figcaption>
      )}
    </figure>
  )
}
